// Centralised axios instance. Responsibilities:
//   1. Attach the in-memory access token as Bearer on every request.
//   2. On 401, fire a single /v1/auth/refresh call and retry the original
//      request. Multiple in-flight 401s share the same refresh promise so
//      we never trigger refresh storms.
//   3. Surface envelope errors as ApiError instances with code/message.
//
// The refresh token lives in an httpOnly Secure SameSite=Lax cookie
// (`luckydesk_refresh`) — JS code in this SPA can't read it; we trust
// the browser to send it with the refresh call.

import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";

export type Envelope<T> = { data: T; error?: never } | { data?: never; error: ApiErrorBody };

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Array<{ field?: string; message: string }>;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: ApiErrorBody["details"],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let accessToken: string | null = null;
let activeOrgID: string | null = null;
let refreshPromise: Promise<string> | null = null;
let onLogout: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// setActiveOrgID pins the superadmin "org switcher" header. When set, every
// outgoing request carries `X-Org-Id: <id>`. The backend honours the header
// only when the caller holds `org:manage_all`, so a regular user setting it
// in dev tools cannot escalate.
export function setActiveOrgID(id: string | null) {
  activeOrgID = id;
}

export function getActiveOrgID(): string | null {
  return activeOrgID;
}

export function registerLogoutHandler(fn: () => void) {
  onLogout = fn;
}

// Build the http instance.
export const api: AxiosInstance = axios.create({
  baseURL: "/",
  withCredentials: true, // for the refresh cookie
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  if (accessToken && !config.headers?.Authorization) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (activeOrgID && !config.headers?.["X-Org-Id"]) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)["X-Org-Id"] = activeOrgID;
  }
  return config;
});

interface RetryableConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _skipRefresh?: boolean;
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError<Envelope<unknown>>) => {
    const cfg = error.config as RetryableConfig | undefined;
    const status = error.response?.status;
    if (status === 401 && cfg && !cfg._retry && !cfg._skipRefresh) {
      cfg._retry = true;
      try {
        const token = await ensureRefresh();
        cfg.headers = cfg.headers ?? {};
        (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        return api.request(cfg);
      } catch (refreshErr) {
        if (onLogout) onLogout();
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(toApiError(error));
  },
);

async function ensureRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const resp = await axios.post<Envelope<{ access_token: string }>>(
        "/v1/auth/refresh",
        {},
        { withCredentials: true },
      );
      const env = resp.data;
      if (!("data" in env) || !env.data) {
        throw new ApiError("REFRESH_FAILED", "no data in refresh response", resp.status);
      }
      accessToken = env.data.access_token;
      return env.data.access_token;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

function toApiError(err: AxiosError<Envelope<unknown>>): ApiError {
  if (err.response?.data && "error" in err.response.data && err.response.data.error) {
    const body = err.response.data.error;
    return new ApiError(body.code, body.message, err.response.status, body.details);
  }
  return new ApiError(
    "NETWORK",
    err.message || "network error",
    err.response?.status ?? 0,
  );
}

// Typed helpers — every endpoint wrapper in services/* calls these so
// the envelope unwrap happens in exactly one place.

export async function getData<T>(url: string, params?: object): Promise<T> {
  const resp = await api.get<Envelope<T>>(url, { params });
  return unwrap(resp.data);
}

export async function postData<T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
  const resp = await api.post<Envelope<T>>(url, body, config);
  return unwrap(resp.data);
}

export async function patchData<T, B = unknown>(url: string, body?: B): Promise<T> {
  const resp = await api.patch<Envelope<T>>(url, body);
  return unwrap(resp.data);
}

export async function putData<T, B = unknown>(url: string, body?: B): Promise<T> {
  const resp = await api.put<Envelope<T>>(url, body);
  return unwrap(resp.data);
}

export async function deleteData<T = void>(url: string, body?: unknown): Promise<T> {
  const resp = await api.delete<Envelope<T>>(url, { data: body });
  if (resp.status === 204) return undefined as T;
  return unwrap(resp.data);
}

function unwrap<T>(env: Envelope<T>): T {
  if ("error" in env && env.error) {
    throw new ApiError(env.error.code, env.error.message, 0, env.error.details);
  }
  return (env as { data: T }).data;
}
